/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CardSkills, ProductCard, breakpoints } from '@coursera/cds-core';
import type {
  ProductCard as ProductCardTrackingData,
  Product as ProductTrackingData,
  ProductType,
  Recommender,
} from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { getCreditType, shouldShowCreditBadge } from 'bundles/product-card/components/LearningProductCardBadge';
import LearningProductCardFooter from 'bundles/product-card/components/LearningProductCardFooter';
import { getMarketingStatusTagForSlug } from 'bundles/product-card/components/utils/copyTestUtils';
import {
  capitalizeAndTranslateEachWordInEntityType,
  eventingV3ProductType,
  extractEventId,
  saveProductCardRecentlyViewed,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import type { AutocompleteProductCardData } from 'bundles/product-card/components/utils/productCardV2Utils';
import { getTopAiProgramExperimentalTag } from 'bundles/product-card/components/utils/topAiProgramPillUtils';
import { getTrendingNowExperimentalTag } from 'bundles/product-card/components/utils/trendingNowPillUtils';
import { PRODUCT_CARD_EXPERIMENT_VARIANT } from 'bundles/product-card/constants';
import { useTopAiProgramPillVariant } from 'bundles/product-card/hooks/useTopAiProgramPillVariant';
import { useTrendingNowPillVariant } from 'bundles/product-card/hooks/useTrendingNowPillVariant';
import { BADGE_NAMES } from 'bundles/product-card/utils/getProductCardStatusTags';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import { getUpsellCopy } from 'bundles/stackability-common/stackability-common-index';

const styles = {
  leftAlignProductCardTags: css`
    ${breakpoints.up('md')} {
      .cds-ProductCard-listCard {
        .cds-ProductCard-statusTags {
          position: absolute;
          left: 0;
          right: unset;
          top: var(--cds-spacing-100);
          padding-left: var(--cds-spacing-100);
          max-width: 298px;
          z-index: 1;
        }
      }
    }
  `,

  rightAlignProductCardTags: css`
    ${breakpoints.up('sm')} {
      .cds-ProductCard-statusTagsOverlay {
        left: unset !important;
        right: 0;
      }
    }
  `,

  cardContainer: css`
    min-height: 200px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: pointer;

    .cds-ProductCard-partnerLogos img {
      vertical-align: unset;
    }

    .cds-CommonCard-previewImage {
      position: relative;
    }

    & div.cds-ProductCard-content div.cds-ProductCard-footer {
      justify-content: flex-end;
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-base.cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,
};

type Tag = string | React.ReactElement;
type LearningProduct = (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];

type Props = {
  adminContent?: React.ReactNode;
  customMetadata?: string[];
  customOnClick?: () => void;
  /** When provided, LearningProductCard records this click to Recently Viewed (autocomplete) itself. */
  autocompleteProductCardData?: AutocompleteProductCardData;
  ImageComponent?: React.ReactNode;
  name: string;
  linkProps?: { href?: string; target?: string; rel?: string; component?: React.ElementType };
  titleComponent?: 'h3' | 'h4';
  partners?: { id: string; logo?: string; name: string }[];
  skills?: string[];
  bodyContent?: React.ReactNode;
  numProductRatings?: number;
  avgProductRating?: number;
  productType?: LearningProduct;
  productDifficultyLevel?: string;
  productDuration?: string;
  statusTags?: { tags: Tag[]; tagNames?: string[] };
  preBadgeContent?: React.ReactNode;
  badgeConfig?: {
    hideCreditBadge?: boolean;
    productType?: LearningProduct;
    isPathwayContent?: boolean;
  };
  statusTagConfig?: {
    hideCreditStatusTag?: boolean;
    hideAllStatusTags?: boolean;
  };
  postBadgeContent?: React.ReactNode;
  variant?: 'list' | 'grid';
  size?: 'compact' | 'standard';
  id?: string;
  slug?: string;
  cardIndex?: number;
  indexName?: string;
  recommender?: Recommender;
  trackingProps?: {
    trackedLinkProps?: {
      trackingName: string;
      withVisibilityTracking: boolean;
      requireFullyVisible: boolean;
      data: Record<string, unknown>;
      refAlt?: React.RefObject<HTMLAnchorElement>;
    };
    additionalProductCardTrackingData?: Partial<ProductCardTrackingData>;
    additionalProductTrackingData?: Partial<ProductTrackingData>;
    additionalV3TrackingData?: Record<string, unknown>;
  };
  isMetadataAboveProductRating?: boolean;
  buttons?: Array<React.ReactElement>;
  subscriptionFooterTag?: string;
  /** Pass-through to LearningProductCardFooter — renders badge after metadata, just above buttons. */
  badgeAfterMetadata?: boolean;
  /** Search-only top match label injected into redesigned footer pill tags. */
  topMatchStatusTagLabel?: string;
};

const LearningProductCard = ({
  adminContent,
  customMetadata,
  customOnClick,
  autocompleteProductCardData,
  ImageComponent,
  name,
  titleComponent,
  linkProps,
  partners,
  skills,
  bodyContent,
  numProductRatings,
  avgProductRating,
  productType,
  productDifficultyLevel,
  productDuration,
  statusTags,
  preBadgeContent,
  badgeConfig,
  statusTagConfig,
  postBadgeContent,
  variant = 'grid',
  size = 'standard',
  id,
  slug,
  cardIndex,
  indexName,
  recommender,
  trackingProps,
  isMetadataAboveProductRating = false,
  buttons,
  subscriptionFooterTag,
  badgeAfterMetadata = false,
  topMatchStatusTagLabel,
}: Props) => {
  const track = useTracker();

  const getTrackingProductId = (productId: string | undefined): string => {
    if (typeof productId !== 'string') {
      return '';
    }
    if (productId.startsWith('child~')) {
      return productId; // Preserve "child~" prefix for product IDs in tracking events
    }
    return extractEventId(productId) || '';
  };

  const marketingTag = slug ? getMarketingStatusTagForSlug(slug) : undefined;
  const topAiProgramPillVariant = useTopAiProgramPillVariant(slug);
  // MERCH-3315: only ever truthy for a slug already categorized 'top-ai-program' in the
  // marketing tag map, so marketingTag has already added this exact "Top AI program" text to
  // tagNames below — this just supplies the gradient/solid renderer for that existing entry,
  // it must never also be prepended as a second tagNames entry (would render the pill twice).
  const topAiProgramExperimentalTag = getTopAiProgramExperimentalTag(topAiProgramPillVariant);
  const trendingNowPillVariant = useTrendingNowPillVariant(slug);
  const trendingNowExperimentalTag = getTrendingNowExperimentalTag(trendingNowPillVariant);
  const experimentalPillTags = [topAiProgramExperimentalTag, trendingNowExperimentalTag].filter(
    (tag): tag is NonNullable<typeof tag> => Boolean(tag)
  );
  const shouldInlineTopMatchInStatusTags = Boolean(topMatchStatusTagLabel);

  const cardMetadata =
    customMetadata ||
    [productDifficultyLevel, capitalizeAndTranslateEachWordInEntityType(productType), productDuration].filter(Boolean);

  // Rendering list cards with even a single status tag can cause styling issues with longer-typed
  // languages for desktop widths(see https://coursera.atlassian.net/browse/MERCH-1660). As a
  // workaround, we left-align tags and assign a max-width slightly less than the preview image.
  const listCardWithStatusTags = variant === 'list' && statusTags?.tags?.length && statusTags?.tags?.length >= 1;

  const combinedBadge = (
    <>
      {preBadgeContent}
      {!shouldInlineTopMatchInStatusTags && postBadgeContent}
    </>
  );

  const creditBadgePillTag =
    !statusTagConfig?.hideCreditStatusTag &&
    shouldShowCreditBadge(badgeConfig?.productType) &&
    getCreditType(badgeConfig?.productType).label;

  const getStatusTagsForFooter = () => {
    if (statusTagConfig?.hideAllStatusTags) {
      return undefined;
    }

    let tagNames = statusTags?.tagNames ?? [];

    if (marketingTag || trendingNowExperimentalTag) {
      // Compliance carve-out: on a sponsored card "Sponsored" REPLACES the experiment tag.
      // We use marketingTag.tag (already translated) for the non-sponsored case.
      // MERCH-3315/MERCH-3316: the marketing-tag master slug map and the trending-now slug list
      // have zero overlapping slugs (verified against both lists), so at most one of
      // marketingTag/trendingNowExperimentalTag is ever truthy for a given card — only one tag is
      // ever prepended here, never both.
      const hasSponsored = statusTags?.tagNames?.includes(BADGE_NAMES.SPONSORED);
      if (hasSponsored) {
        tagNames = [BADGE_NAMES.SPONSORED];
      } else {
        tagNames = trendingNowExperimentalTag ? [trendingNowExperimentalTag.tag, ...tagNames] : tagNames;
        tagNames = marketingTag ? [marketingTag.tag, ...tagNames] : tagNames;
      }
    } else if (badgeConfig?.isPathwayContent) {
      tagNames = [...tagNames, getUpsellCopy().homepagesNavXdp.buildTowardADegree];
    } else if (creditBadgePillTag) {
      tagNames = [...tagNames, creditBadgePillTag];
    }

    if (subscriptionFooterTag) {
      tagNames = [subscriptionFooterTag, ...tagNames];
    }

    if (shouldInlineTopMatchInStatusTags && topMatchStatusTagLabel) {
      const capped = [topMatchStatusTagLabel, ...tagNames].slice(0, 2);
      // The 2-pill cap must never evict the mandatory Sponsored disclosure.
      if (tagNames.includes(BADGE_NAMES.SPONSORED) && !capped.includes(BADGE_NAMES.SPONSORED)) {
        return [topMatchStatusTagLabel, BADGE_NAMES.SPONSORED];
      }
      return capped;
    }

    return tagNames;
  };

  const trackingData: EventData<'click_product_card'> = {
    product: {
      id: getTrackingProductId(id),
      name,
      slug: slug ?? linkProps?.href ?? '',
      type: eventingV3ProductType(productType) as ProductType,
      ...trackingProps?.additionalProductTrackingData,
    },
    productCard: {
      ...(typeof trackingProps?.trackedLinkProps?.data.productCard === 'object'
        ? trackingProps?.trackedLinkProps?.data.productCard
        : {}),
      index: cardIndex ?? 0,
      ...(indexName && { algoliaIndexName: indexName }),
      ...(recommender && { recommender }),
      tags: getStatusTagsForFooter(),
      ...(trackingProps?.additionalProductCardTrackingData?.pageConfigComponent && {
        pageConfigComponent: trackingProps?.additionalProductCardTrackingData?.pageConfigComponent,
      }),
    },
    ...trackingProps?.additionalV3TrackingData,
  };

  const trackedCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  return (
    <div
      css={[
        styles.cardContainer,
        listCardWithStatusTags ? styles.leftAlignProductCardTags : styles.rightAlignProductCardTags,
        variant === 'list' ? styles.listCardContainer : null,
      ]}
      data-testid="product-card-cds"
    >
      <ProductCard
        experimentVariant={PRODUCT_CARD_EXPERIMENT_VARIANT}
        onClick={(event) => {
          track('click_product_card', trackingData);
          if (linkProps?.href) {
            // Only record Recently Viewed when the click actually navigates to the product's page —
            // customOnClick below covers non-navigating interactions (e.g. admin pickers, in-place modals).
            if (autocompleteProductCardData && name) {
              saveProductCardRecentlyViewed(
                { id, name, slug, path: linkProps.href, productType, isPathwayProduct: badgeConfig?.isPathwayContent },
                autocompleteProductCardData
              );
            }
            return;
          }
          if (customOnClick) {
            event.preventDefault();
            event.stopPropagation();
            customOnClick();
          }
        }}
        ref={trackedCardRef}
        renderPreviewImage={() => ImageComponent}
        title={{
          name,
          linkProps,
          component: titleComponent,
          customLinkProps: trackingProps?.trackedLinkProps,
        }}
        partners={partners}
        body={
          (skills && skills.length > 0) || bodyContent ? (
            <>
              {skills && skills.length > 0 && <CardSkills skills={skills} />}
              {bodyContent}
            </>
          ) : undefined
        }
        footer={
          <LearningProductCardFooter
            isMetadataAboveProductRating={isMetadataAboveProductRating}
            cardMetadata={cardMetadata}
            avgProductRating={avgProductRating}
            numProductRatings={numProductRatings}
            badge={combinedBadge}
            shouldUseColorCodedPillTags
            statusTagNames={getStatusTagsForFooter()}
            buttons={buttons}
            productCardVariant={variant}
            badgeAfterMetadata={badgeAfterMetadata}
            topMatchLabel={shouldInlineTopMatchInStatusTags ? topMatchStatusTagLabel : undefined}
            experimentalPillTags={experimentalPillTags}
          />
        }
        variant={variant}
        size={size}
        productType={productType || ''}
        adminContent={adminContent}
      />
    </div>
  );
};

export default LearningProductCard;
